import React from 'react';
import { BaseConfigProps, commonStyles, validateCron } from '../shared/ConfigFormHelpers';

const ScheduleTriggerConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const cronValue = node.data.config.cron || '0 9 * * 1-5';
  const isValidCron = validateCron(cronValue);

  const cronPresets = [
    // Másodperces időzítések (teszteléshez)
    { value: '*/10 * * * * *', label: '🚀 10 másodpercenként', desc: 'Gyors teszt', category: 'testing' },
    { value: '*/30 * * * * *', label: '🚀 30 másodpercenként', desc: 'Email teszt', category: 'testing' },
    // Gyakori időzítések
    { value: '* * * * *', label: '⚡ Percenként', desc: 'Minden percben fut', category: 'frequent' },
    { value: '*/2 * * * *', label: '⚡ 2 percenként', desc: 'Minden 2. percben', category: 'frequent' },
    { value: '*/5 * * * *', label: '⚡ 5 percenként', desc: 'Minden 5. percben', category: 'frequent' },
    { value: '*/10 * * * *', label: '⚡ 10 percenként', desc: 'Minden 10. percben', category: 'frequent' },
    { value: '*/15 * * * *', label: '⚡ 15 percenként', desc: 'Minden 15. percben', category: 'frequent' },
    { value: '*/30 * * * *', label: '⚡ 30 percenként', desc: 'Fél óránként', category: 'frequent' },
    { value: '0 * * * *', label: '🕐 Óránként', desc: 'Minden óra kezdetén', category: 'frequent' },
    { value: '0 */2 * * *', label: '🕐 2 óránként', desc: 'Minden második órában', category: 'frequent' },
    { value: '0 */3 * * *', label: '🕐 3 óránként', desc: 'Minden harmadik órában', category: 'frequent' },
    { value: '0 */6 * * *', label: '🕐 6 óránként', desc: 'Naponta 4-szer', category: 'frequent' },
    { value: '0 */12 * * *', label: '🕐 12 óránként', desc: 'Naponta 2-szer', category: 'frequent' },
    // Napi időzítések
    { value: '0 9 * * 1-5', label: '📅 Hétköznap 9:00', desc: 'Hétfőtől péntekig reggel 9-kor', category: 'daily' },
    { value: '0 0 * * *', label: '🌙 Naponta éjfélkor', desc: 'Minden nap 0:00-kor', category: 'daily' },
    { value: '0 6 * * *', label: '🌅 Naponta 6:00', desc: 'Minden reggel 6 órakor', category: 'daily' },
    { value: '0 12 * * *', label: '☀️ Naponta délben', desc: 'Minden nap 12:00-kor', category: 'daily' },
    { value: '0 18 * * *', label: '🌆 Naponta 18:00', desc: 'Minden este 6 órakor', category: 'daily' },
    // Heti/Havi
    { value: '0 9 * * 1', label: '📆 Hétfőnként 9:00', desc: 'Minden hétfő reggel', category: 'weekly' },
    { value: '0 0 * * 0', label: '📆 Vasárnaponként', desc: 'Minden vasárnap éjfélkor', category: 'weekly' },
    { value: '0 0 1 * *', label: '📆 Havonta', desc: 'Minden hónap 1-jén', category: 'monthly' }
  ];

  return (
    <div className="space-y-3">
      {/* Ütemezés státusz */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            ⏰ Ez a flow automatikusan fut az ütemezés szerint
          </span>
        </div>
        {cronValue && (
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
            <strong>Cron:</strong> <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">{cronValue}</code>
          </p>
        )}
      </div>

      <div>
        <label className={commonStyles.label}>
          Cron kifejezés
        </label>
        <input
          type="text"
          value={cronValue}
          onChange={(e) => updateConfig('cron', e.target.value)}
          className={`${commonStyles.input} ${!isValidCron ? 'border-red-500 focus:ring-red-500' : ''}`}
          placeholder="0 9 * * 1-5"
        />
        {!isValidCron && (
          <p className="text-xs text-red-500 mt-1">
            ⚠️ Érvénytelen cron kifejezés
          </p>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Formátum: perc óra nap hónap hét_napja
        </p>
      </div>

      <div>
        <label className={commonStyles.label}>
          Időzóna
        </label>
        <select
          value={node.data.config.timezone || 'Europe/Budapest'}
          onChange={(e) => updateConfig('timezone', e.target.value)}
          className={commonStyles.select}
        >
          <option value="Europe/Budapest">Budapest (CET/CEST)</option>
          <option value="UTC">UTC</option>
          <option value="Europe/London">London (GMT/BST)</option>
          <option value="America/New_York">New York (EST/EDT)</option>
          <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
          <option value="Asia/Tokyo">Tokyo (JST)</option>
        </select>
      </div>

      {/* Cron presets - Kategóriákkal */}
      <div>
        <label className={commonStyles.smallLabel}>
          Gyors beállítások
        </label>
        
        {/* Tesztelési időzítések */}
        <div className="mb-3">
          <h4 className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-2">
            🚀 Teszteléshez (másodpercek)
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {cronPresets.filter(p => p.category === 'testing').map((preset) => (
              <button
                key={preset.value}
                onClick={() => updateConfig('cron', preset.value)}
                className={`text-left p-2 rounded-md border transition-colors ${
                  cronValue === preset.value
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-orange-300 dark:border-orange-600 hover:border-orange-400 dark:hover:border-orange-500'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {preset.label}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {preset.desc}
                </p>
              </button>
            ))}
          </div>
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
            ⚠️ Csak teszteléshez! Production-ben ne használd.
          </p>
        </div>
        
        {/* Gyakori időzítések */}
        <div className="mb-3">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            ⚡ Gyakori időzítések
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {cronPresets.filter(p => p.category === 'frequent').map((preset) => (
              <button
                key={preset.value}
                onClick={() => updateConfig('cron', preset.value)}
                className={`text-left p-2 rounded-md border transition-colors ${
                  cronValue === preset.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {preset.label}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {preset.desc}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Napi időzítések */}
        <div className="mb-3">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            📅 Napi időzítések
          </h4>
          <div className="grid grid-cols-1 gap-2">
            {cronPresets.filter(p => p.category === 'daily').map((preset) => (
              <button
                key={preset.value}
                onClick={() => updateConfig('cron', preset.value)}
                className={`text-left p-2 rounded-md border transition-colors ${
                  cronValue === preset.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {preset.label}
                  </span>
                  <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {preset.value}
                  </code>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {preset.desc}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Heti/Havi időzítések */}
        <div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            📆 Heti/Havi időzítések
          </h4>
          <div className="grid grid-cols-1 gap-2">
            {cronPresets.filter(p => p.category === 'weekly' || p.category === 'monthly').map((preset) => (
              <button
                key={preset.value}
                onClick={() => updateConfig('cron', preset.value)}
                className={`text-left p-2 rounded-md border transition-colors ${
                  cronValue === preset.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {preset.label}
                  </span>
                  <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {preset.value}
                  </code>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {preset.desc}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cron segítség */}
      <div className={commonStyles.card}>
        <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-2">
          📚 Cron segítség
        </h4>
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
          <div><code>*</code> - minden érték</div>
          <div><code>*/5</code> - minden 5. érték</div>
          <div><code>1-5</code> - 1-től 5-ig</div>
          <div><code>1,3,5</code> - 1, 3 és 5</div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTriggerConfig;
